import type { ReactNode } from 'react';
import { useConfig } from '../lib/config.js';

/**
 * Logotipo da clínica.
 *
 * Componente único porque a marca aparece em cinco lugares (barra lateral, topo
 * mobile, TV, pareamento, expediente encerrado) e o design é explícito sobre
 * **qual variante** vale em cada fundo. Espalhar `<img>` pela interface levaria,
 * mais cedo ou mais tarde, à variante errada sobre o fundo errado.
 *
 * O caminho vem da configuração da clínica, nunca de `import` de imagem: é o que
 * permite vestir outro cliente trocando `config/<cliente>` (Guia §8, Cláusula 7).
 *
 * A versão anterior desenhava um monograma com a inicial do nome. Era um marcador
 * de posição enquanto o design não tinha chegado — e o design tem logotipo
 * próprio, com o dente dentro do "Q".
 */
export function Logo({
  variant = 'default',
  className,
}: {
  /** `onDark` só para fundo institucional teal — não é tema escuro. */
  readonly variant?: 'default' | 'onDark';
  readonly className?: string;
}): ReactNode {
  const config = useConfig();
  const src = variant === 'onDark' ? config.logo.onDark : config.logo.default;

  // Sem caminho configurado não há logotipo — e uma `<img>` com `src` vazio é um
  // ícone de imagem quebrada, que numa TV de sala de espera é pior que a ausência.
  // Acontece só no fallback de `/api/config` fora do ar.
  if (src === '') return null;

  return (
    <img
      src={src}
      // O nome da clínica já aparece como texto ao lado em toda superfície onde o
      // logotipo é decorativo; aqui ele carrega o nome porque em várias telas
      // (TV, pareamento) é a única menção à marca.
      alt={config.clinicName}
      className={className}
      // Dimensões intrínsecas evitam o pulo de layout enquanto a imagem carrega —
      // numa TV que fica ligada o dia todo, o pulo aparece a cada recarregamento.
      width={512}
      height={128}
    />
  );
}
